import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/admin-api";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { DEFAULT_PIPELINE, type StageDef, makeStageKey } from "@ow/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronUp,
  GripVertical,
  Lock,
  Plus,
  RotateCcw,
  Trash2,
  TriangleAlert,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";

const MAX_STAGES = 12;

/** True when two ladders differ in keys, labels, or order. */
function sameLadder(a: StageDef[], b: StageDef[]): boolean {
  return a.length === b.length && a.every((s, i) => s.key === b[i]?.key && s.label === b[i]?.label);
}

/** Validation issues for a proposed ladder — surfaced inline, and used to gate save/launch. */
function validate(stages: StageDef[]): string | null {
  if (stages.length === 0) return "A pipeline needs at least one stage.";
  if (stages.some((s) => !s.label.trim())) return "Every stage needs a name.";
  const seen = new Set<string>();
  for (const s of stages) {
    const l = s.label.trim().toLowerCase();
    if (seen.has(l)) return `Two stages are both named “${s.label.trim()}”. Stage names must be unique.`;
    seen.add(l);
  }
  return null;
}

/** The locked, post-launch pipeline: read-only stage flow with a clear "why it's locked" note. Used by
 *  the legacy `/admin/*` back-office only — the canonical role page folds the lock status into its
 *  pipeline tracker instead of re-listing the ladder. Pipelines are pre-launch-only; no edit here. */
export function LockedPipelineView({ stages, launchedAt }: { stages: StageDef[]; launchedAt: string | null }) {
  const when = launchedAt ? new Date(launchedAt).toLocaleDateString(undefined, { dateStyle: "medium" }) : null;
  return (
    <div className="space-y-3 rounded-xl border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="eyebrow">Pipeline</span>
        <span className="inline-flex items-center gap-1.5 rounded-full border bg-muted px-2.5 py-1 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
          <Lock className="size-3" /> Locked
        </span>
      </div>
      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
        {stages.map((s, i) => (
          <li key={s.key} className="flex items-center gap-1.5">
            <span className="inline-flex items-center gap-2 rounded-md border bg-background px-2.5 py-1.5 text-sm">
              <span className="text-[11px] tabular-nums text-muted-foreground">{i + 1}</span>
              {s.label}
            </span>
            {i < stages.length - 1 ? <ChevronDown className="size-3.5 -rotate-90 text-muted-foreground/40" /> : null}
          </li>
        ))}
      </ol>
      <p className="text-xs text-muted-foreground">
        This {stages.length}-stage pipeline locked when the role went live{when ? ` on ${when}` : ""}. Stages can't be
        added, renamed, reordered, or removed after launch — candidates move through these stages for the life of the
        search.
      </p>
    </div>
  );
}

/** Admin-only, pre-launch pipeline builder: add / rename / reorder / remove stages starting from the
 *  default ladder, then launch the role — which locks the pipeline for good. Reorder works by drag
 *  handle AND by up/down buttons (keyboard-accessible); launch is gated behind an explicit
 *  irreversible-action confirmation that states the consequence up front. */
export function PipelineConfigurator({
  searchId,
  initialStages,
}: {
  searchId: string;
  initialStages: StageDef[];
}) {
  const qc = useQueryClient();
  const [stages, setStages] = useState<StageDef[]>(initialStages);
  const [saved, setSaved] = useState<StageDef[]>(initialStages);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [overIdx, setOverIdx] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const lastRowRef = useRef<HTMLInputElement | null>(null);
  const focusLast = useRef(false);

  // Focus the newly added stage's name field so the admin can type straight away. Depends on
  // `stages` deliberately: we refocus only after the list grows by an Add.
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run when the stage list changes
  useEffect(() => {
    if (focusLast.current) {
      lastRowRef.current?.focus();
      lastRowRef.current?.select();
      focusLast.current = false;
    }
  }, [stages]);

  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["admin", "search-detail", searchId] }),
      qc.invalidateQueries({ queryKey: ["client", "search", searchId] }),
    ]);

  const saveM = useMutation({
    mutationFn: (next: StageDef[]) => adminApi.searches.setPipeline(searchId, next),
    onSuccess: async (_row, next) => {
      setSaved(next);
      await invalidate();
    },
  });
  const launchM = useMutation({
    mutationFn: () => adminApi.searches.launch(searchId),
    onSuccess: async () => {
      setConfirmOpen(false);
      await invalidate();
    },
  });

  const error = validate(stages);
  const dirty = !sameLadder(stages, saved);

  const rename = (i: number, label: string) => setStages((prev) => prev.map((s, j) => (j === i ? { ...s, label } : s)));
  const remove = (i: number) => setStages((prev) => prev.filter((_, j) => j !== i));
  const move = (i: number, dir: -1 | 1) =>
    setStages((prev) => {
      const j = i + dir;
      const a = prev[i];
      const b = prev[j];
      if (!a || !b) return prev;
      const next = [...prev];
      next[i] = b;
      next[j] = a;
      return next;
    });
  const add = () => {
    focusLast.current = true;
    setStages((prev) => [
      ...prev,
      {
        key: makeStageKey(
          "New stage",
          prev.map((s) => s.key),
        ),
        label: "New stage",
      },
    ]);
  };
  const reset = () => setStages(DEFAULT_PIPELINE);

  const reorder = (from: number, to: number) =>
    setStages((prev) => {
      if (from === to) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      if (!moved) return prev;
      next.splice(to, 0, moved);
      return next;
    });

  const onSave = () => {
    if (!error) saveM.mutate(stages);
  };
  const onConfirmLaunch = () => {
    // Persist any unsaved edits first, then go live — so the locked pipeline is exactly what's shown.
    if (dirty && !error) {
      saveM.mutate(stages, { onSuccess: () => launchM.mutate() });
    } else {
      launchM.mutate();
    }
  };

  const mutationError =
    saveM.error instanceof ApiError
      ? saveM.error.message
      : launchM.error instanceof ApiError
        ? launchM.error.message
        : null;

  return (
    <div className="space-y-3 rounded-xl border bg-card p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="eyebrow">Pipeline</span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--brand-accent)] bg-[var(--brand-accent)]/15 px-2.5 py-1 font-mono text-[11px] uppercase tracking-wide text-foreground">
          Draft
        </span>
      </div>
      <p className="text-xs text-muted-foreground">
        Set the stages a candidate moves through. Rename, reorder, add or remove stages now — then launch the role.
        <span className="text-foreground"> Launching locks the pipeline permanently.</span>
      </p>

      {/* Stage list */}
      <ol className="divide-y overflow-hidden rounded-lg border">
        {stages.map((s, i) => {
          const isOver = overIdx === i && dragIdx !== null && dragIdx !== i;
          return (
            <li
              key={s.key}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragEnter={() => setOverIdx(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragIdx !== null) reorder(dragIdx, i);
                setDragIdx(null);
                setOverIdx(null);
              }}
              onDragEnd={() => {
                setDragIdx(null);
                setOverIdx(null);
              }}
              className={cn(
                "flex items-center gap-2 bg-card px-2.5 py-2 transition-colors",
                dragIdx === i && "opacity-50",
                isOver && "bg-accent",
              )}
            >
              <button
                type="button"
                aria-label="Drag to reorder"
                className="cursor-grab touch-none text-muted-foreground/50 hover:text-foreground active:cursor-grabbing"
              >
                <GripVertical className="size-4" />
              </button>
              <span className="grid size-6 shrink-0 place-items-center rounded-full bg-muted text-[11px] tabular-nums text-muted-foreground">
                {i + 1}
              </span>
              <Input
                ref={i === stages.length - 1 ? lastRowRef : undefined}
                value={s.label}
                onChange={(e) => rename(i, e.target.value)}
                aria-label={`Stage ${i + 1} name`}
                className="h-9 flex-1"
              />
              <div className="flex shrink-0 items-center">
                <Button size="xs" variant="ghost" aria-label="Move up" disabled={i === 0} onClick={() => move(i, -1)}>
                  <ChevronUp className="size-4" />
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  aria-label="Move down"
                  disabled={i === stages.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <ChevronDown className="size-4" />
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  aria-label="Remove stage"
                  disabled={stages.length <= 1}
                  onClick={() => remove(i)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            </li>
          );
        })}
      </ol>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="secondary" onClick={add} disabled={stages.length >= MAX_STAGES}>
          <Plus className="size-4" /> Add stage
        </Button>
        <Button size="sm" variant="ghost" onClick={reset} disabled={sameLadder(stages, DEFAULT_PIPELINE)}>
          <RotateCcw className="size-3.5" /> Reset to default
        </Button>
        <span className="eyebrow ml-auto">
          {stages.length} stage{stages.length === 1 ? "" : "s"}
        </span>
      </div>

      {error ? (
        <p className="flex items-center gap-1.5 text-xs text-[var(--color-warning)]">
          <TriangleAlert className="size-3.5" /> {error}
        </p>
      ) : null}
      {mutationError ? (
        <p className="flex items-center gap-1.5 text-xs text-destructive">
          <TriangleAlert className="size-3.5" /> {mutationError}
        </p>
      ) : null}

      {/* Action bar */}
      <div className="flex flex-wrap items-center gap-2 border-t pt-3">
        <span className="text-xs text-muted-foreground">
          {saveM.isPending ? (
            "Saving…"
          ) : dirty ? (
            "Unsaved changes"
          ) : (
            <span className="inline-flex items-center gap-1 text-[var(--color-success)]">
              <Check className="size-3.5" /> Draft saved
            </span>
          )}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="secondary" onClick={onSave} disabled={!dirty || !!error || saveM.isPending}>
            Save draft
          </Button>
          <Button size="sm" onClick={() => setConfirmOpen(true)} disabled={!!error || launchM.isPending}>
            Launch role <ArrowRight className="size-4" />
          </Button>
        </div>
      </div>

      {confirmOpen ? (
        <LaunchConfirm
          stageCount={stages.length}
          pending={saveM.isPending || launchM.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={onConfirmLaunch}
        />
      ) : null}
    </div>
  );
}

/** Irreversible-action confirmation: states the consequence up front (error prevention), not after. */
function LaunchConfirm({
  stageCount,
  pending,
  onConfirm,
  onCancel,
}: {
  stageCount: number;
  pending: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-foreground/40 p-4 backdrop-blur-sm"
      onClick={onCancel}
    >
      <dialog
        open
        aria-labelledby="launch-title"
        className="relative w-full max-w-md space-y-4 rounded-xl border bg-card p-6 text-foreground shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3">
          <span className="grid size-9 shrink-0 place-items-center rounded-full bg-[var(--brand-accent)]/20 text-foreground">
            <Lock className="size-4" />
          </span>
          <div className="space-y-1">
            <h2 id="launch-title" className="text-base font-semibold">
              Launch role and lock the pipeline?
            </h2>
            <p className="text-sm text-muted-foreground">
              The role goes live with these <strong className="text-foreground">{stageCount} stages</strong>. After
              launch the pipeline is <strong className="text-foreground">permanently locked</strong> — you won't be able
              to add, rename, reorder, or remove stages. This can't be undone.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={onConfirm} disabled={pending}>
            {pending ? "Launching…" : "Launch & lock"}
          </Button>
        </div>
      </dialog>
    </div>
  );
}
